import React, { useState } from 'react';
import { submitJobApplication, type Job } from '../../admin/api';

interface JobApplyModalProps {
  job: Job;
  onClose: () => void;
}

const JobApplyModal: React.FC<JobApplyModalProps> = ({ job, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    portfolio: '',
    coverLetter: ''
  });
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cvFile) {
      setSubmitError('Please upload your CV (PDF only)');
      return;
    }
    
    setSubmitError('');
    setIsSubmitting(true);
    
    try {
      const data = new FormData();
      data.append('name', formData.name);
      data.append('email', formData.email);
      data.append('phone', formData.phone);
      data.append('portfolio', formData.portfolio);
      data.append('coverLetter', formData.coverLetter);
      data.append('cv', cvFile);
      
      await submitJobApplication(job._id, data);
      setIsSubmitted(true);
    } catch (err: any) {
      setSubmitError(err.message || 'Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
      backgroundColor: 'rgba(0, 0, 0, 0.6)', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        background: 'white', padding: '40px', borderRadius: '12px',
        width: '100%', maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto',
        position: 'relative', boxShadow: '0 10px 25px rgba(0,0,0,0.2)'
      }}>
        <button 
          onClick={onClose}
          style={{ position: 'absolute', top: '20px', right: '20px', background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#64748b' }}
        >
          &times;
        </button>

        {isSubmitted ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <div style={{ width: '60px', height: '60px', background: '#22c55e', color: 'white', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '30px', margin: '0 auto 20px' }}>
              ✓
            </div>
            <h2 style={{ marginBottom: '10px' }}>Application Received!</h2>
            <p>Thank you for applying for the <strong>{job.title}</strong> position. We will review your application and get back to you soon.</p>
            <button onClick={onClose} className="btn-apply-primary" style={{ marginTop: '20px', padding: '10px 24px', fontSize: '1rem', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Close</button>
          </div>
        ) : (
          <div>
            <h1 style={{ margin: '0 0 10px 0', fontSize: '1.75rem', paddingRight: '30px' }}>Apply for {job.title}</h1>
            <p style={{ color: '#64748b', marginBottom: '25px' }}>{job.department} • {job.city}</p>
            
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600 }}>Full Name *</label>
                <input type="text" name="name" required value={formData.name} onChange={handleChange} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }} />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600 }}>Email Address *</label>
                <input type="email" name="email" required value={formData.email} onChange={handleChange} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }} />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600 }}>Phone Number</label>
                <input type="tel" name="phone" value={formData.phone} onChange={handleChange} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }} />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600 }}>LinkedIn / Portfolio URL</label>
                <input type="url" name="portfolio" value={formData.portfolio} onChange={handleChange} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }} />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600 }}>Why are you a good fit? *</label>
                <textarea name="coverLetter" required value={formData.coverLetter} onChange={handleChange} rows={4} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', resize: 'vertical' }}></textarea>
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Upload CV (PDF only, max 2MB) *</label>
                <input 
                  type="file" 
                  accept="application/pdf" 
                  required 
                  onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) {
                      if (file.size > 2 * 1024 * 1024) {
                        setSubmitError('File is too large. Max size is 2MB.');
                        setCvFile(null);
                        e.target.value = '';
                      } else {
                        setCvFile(file);
                        setSubmitError('');
                      }
                    }
                  }}
                  style={{ 
                    width: '100%', padding: '8px', border: '1px solid #cbd5e1', 
                    borderRadius: '8px', backgroundColor: '#f8fafc' 
                  }}
                />
              </div>

              {submitError && <p style={{ color: '#dc2626', margin: 0, fontSize: '14px' }}>{submitError}</p>}

              <button type="submit" disabled={isSubmitting} style={{ 
                width: '100%', background: '#ea580c', color: 'white', padding: '12px', border: 'none', 
                borderRadius: '8px', cursor: isSubmitting ? 'not-allowed' : 'pointer', 
                fontSize: '1.1rem', fontWeight: 600, opacity: isSubmitting ? 0.7 : 1, marginTop: '10px'
              }}>
                {isSubmitting ? 'Submitting...' : 'Submit Application'}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobApplyModal;
